// App-side tier display data for the Plan / upgrade UI.
// Prices are all-inclusive INR (no GST). Keep in sync with worker/lib/plans.js
// (the enforcement source of truth) and the marketing site.

export type PlanKey = "FREE" | "STARTER" | "STUDIO" | "PRO" | "AGENCY";

export type PlanCard = {
  key: PlanKey;
  name: string;
  priceLabel: string;
  period: string;
  annualPriceLabel: string;   // yearly total (monthly x12 x0.9, floored). Free = same as priceLabel.
  tagline: string;
  storage: string;
  members: string;
  features: string[];
  featured?: boolean;
};

export const PLAN_ORDER: PlanKey[] = ["FREE", "STARTER", "STUDIO", "PRO", "AGENCY"];

export const PLAN_CARDS: PlanCard[] = [
  {
    key: "FREE",
    name: "Free",
    priceLabel: "₹0",
    annualPriceLabel: "₹0",
    period: "forever",
    tagline: "Try the full review flow.",
    storage: "2 GB",
    members: "2 members",
    features: [
      "Unlimited reviewers & clients",
      "2 projects · 100 review links",
      "Comments, annotations, approvals & versions",
      "Public share links",
      "Files & links expire after 14 days",
    ],
  },
  {
    key: "STARTER",
    name: "Starter",
    priceLabel: "₹599",
    annualPriceLabel: "₹6,469",
    period: "/mo",
    tagline: "For freelancers shipping regularly.",
    storage: "150 GB",
    members: "5 members",
    features: [
      "Everything in Free, kept (no auto-delete)",
      "Unlimited projects & links",
      "Share controls: disable downloads/comments, password",
      "Adjustable link expiry",
      "Google Drive import",
      "Email support",
    ],
  },
  {
    key: "STUDIO",
    name: "Studio",
    priceLabel: "₹1,699",
    annualPriceLabel: "₹18,349",
    period: "/mo",
    tagline: "For small teams and studios.",
    storage: "500 GB",
    members: "15 members",
    features: [
      "Everything in Starter",
      "Restricted projects & folders",
      "Internal (team-only) comments",
      "Custom-branded share pages",
      "Priority-lane support",
    ],
    featured: true,
  },
  {
    key: "PRO",
    name: "Pro",
    priceLabel: "₹2,999",
    annualPriceLabel: "₹32,389",
    period: "/mo",
    tagline: "For bigger teams and heavier libraries.",
    storage: "1 TB",
    members: "25 members",
    features: [
      "Everything in Studio",
      "1 TB storage · 25 members",
      "Same studio controls at scale",
      "Priority-lane support",
    ],
  },
  {
    key: "AGENCY",
    name: "Agency",
    priceLabel: "₹4,699",
    annualPriceLabel: "₹50,749",
    period: "/mo",
    tagline: "For agencies running many clients.",
    storage: "1.75 TB",
    members: "Unlimited members",
    features: [
      "Everything in Pro",
      "Authorized email domains",
      "Unlimited members",
      "Priority support",
    ],
  },
];

// ── Full feature-comparison matrix ──────────────────────────────────────────
// Cell order matches PLAN_ORDER: [Free, Starter, Studio, Pro, Agency].
// true = included (✓), false = not included (—), string = a value.
// Keep in sync with worker/lib/plans.js + the marketing site.
export type CompareCell = string | boolean;
export type CompareRow = { label: string; cells: CompareCell[] };
export type CompareGroup = { group: string; rows: CompareRow[] };

export const COMPARE_GROUPS: CompareGroup[] = [
  {
    group: "Limits",
    rows: [
      { label: "Storage", cells: ["2 GB", "150 GB", "500 GB", "1 TB", "1.75 TB"] },
      { label: "Team members", cells: ["2", "5", "15", "25", "Unlimited"] },
      { label: "Reviewers & clients", cells: ["Unlimited", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
      { label: "Projects", cells: ["2", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
      { label: "Review links (YouTube/Vimeo)", cells: ["100", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
      { label: "File retention", cells: ["14-day auto-delete", "Kept", "Kept", "Kept", "Kept"] },
    ],
  },
  {
    group: "Collaboration",
    rows: [
      { label: "Timestamped comments & annotations", cells: [true, true, true, true, true] },
      { label: "@mentions, custom fields, status & approvals", cells: [true, true, true, true, true] },
      { label: "Version stacks & compare", cells: [true, true, true, true, true] },
      { label: "Keyboard shortcuts", cells: [true, true, true, true, true] },
      { label: "Public share links", cells: [true, true, true, true, true] },
    ],
  },
  {
    group: "Share controls",
    rows: [
      { label: "Disable downloads / comments per link", cells: [false, true, true, true, true] },
      { label: "Password-protected shares", cells: [false, true, true, true, true] },
      { label: "Adjustable link expiry", cells: ["5 days", true, true, true, true] },
      { label: "Google Drive import", cells: [false, true, true, true, true] },
    ],
  },
  {
    group: "Access & branding",
    rows: [
      { label: "Restricted projects & folders", cells: [false, false, true, true, true] },
      { label: "Internal (team-only) comments", cells: [false, false, true, true, true] },
      { label: "Custom-branded share pages", cells: [false, false, true, true, true] },
      { label: "Authorized email domains", cells: [false, false, false, false, true] },
    ],
  },
  {
    group: "Support",
    rows: [
      { label: "Support", cells: ["Community", "Email", "Priority-lane", "Priority-lane", "Priority"] },
    ],
  },
];
